import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import * as m from '../../localization/out/messages.js';

/**
 * Shared modal frame: full-bleed bottom sheet on mobile, centered card on tablet+.
 * Click-outside closes; the inner card stops propagation. Title + close icon in the
 * header, content area scrolls, footer pinned with a safe-area-aware bottom inset
 * so iOS doesn't put the action button under the home-indicator gesture strip.
 */
const ActionModalShell = (params: {
  iconClass: string;
  title: string;
  subtitle?: string;
  onClose: () => void;
  body: unknown;
  footer: unknown;
}) => html`
  <div
    class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
    @click=${params.onClose}
  >
    <div
      class="bg-base-100 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col max-h-[90vh]"
      @click=${(e: Event) => e.stopPropagation()}
    >
      <header class="px-5 pt-5 pb-3 flex items-start gap-3">
        <span class="${params.iconClass} text-2xl text-primary mt-0.5"></span>
        <div class="flex-1 min-w-0">
          <h3 class="text-lg font-bold leading-tight">${params.title}</h3>
          ${params.subtitle
            ? html`<p class="text-sm text-base-content/60 mt-0.5">${params.subtitle}</p>`
            : nothing}
        </div>
        <button
          type="button"
          class="btn btn-ghost btn-sm btn-circle -mr-2 -mt-2"
          aria-label="${m.dialog_cancel()}"
          @click=${params.onClose}
        >
          <span class="icon-[material-symbols--close]"></span>
        </button>
      </header>
      <div class="px-5 py-3 flex flex-col gap-4 overflow-y-auto">${params.body}</div>
      <footer
        class="px-5 pt-3 flex justify-end gap-2 border-t border-base-300/60 [padding-bottom:max(--spacing(5),env(safe-area-inset-bottom))]"
      >
        ${params.footer}
      </footer>
    </div>
  </div>
`;

/**
 * Optional free-text notes for the service desk, shared by the exception modals.
 * Sent as `payload.reason` on the resulting ticket event so the desk doesn't have to
 * call the FE/customer afterwards to ask why something didn't work.
 */
const NotesField = (params: {
  id: string;
  value: string;
  busy: boolean;
  onInput: (value: string) => void;
}) => html`
  <div class="form-control">
    <label for="${params.id}" class="text-sm font-medium pb-1">${m.exception_notes_label()}</label>
    <textarea
      id="${params.id}"
      name="${params.id}"
      class="textarea textarea-bordered w-full"
      rows="3"
      placeholder="${m.exception_notes_placeholder()}"
      ?disabled=${params.busy}
      .value=${params.value}
      @input=${(e: Event) => params.onInput((e.target as HTMLTextAreaElement).value)}
    ></textarea>
  </div>
`;

/** "Customer arranged a new date" — full-form modal. */
export interface CadModalValues {
  agreedDate: string;
  securedSite: boolean;
  /** Who on the customer side confirmed the new date — at least one of name/phone is required. */
  confirmedByName: string;
  confirmedByPhone: string;
  newContactName: string;
  newContactPhone: string;
  notes: string;
}

export const CadModal = stateful(
  ({
    busy,
    error,
    onClose,
    onSubmit,
    title,
    subtitle,
    submitLabel,
    showNotes = true,
    showSecuredSite = true,
  }: {
    busy: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (values: CadModalValues) => void;
    title?: string;
    subtitle?: string;
    submitLabel?: string;
    /** Hidden when CUS re-uses this form — the CUS flow already collected notes in its first step. */
    showNotes?: boolean;
    /** The toggle fires `access_not_granted` (CRA) — hidden when the customer disabled CRA. */
    showSecuredSite?: boolean;
  }) => {
    const [agreedDate, setAgreedDate] = useState('');
    const [securedSite, setSecuredSite] = useState(false);
    const [confirmedByName, setConfirmedByName] = useState('');
    const [confirmedByPhone, setConfirmedByPhone] = useState('');
    const [newContactName, setNewContactName] = useState('');
    const [newContactPhone, setNewContactPhone] = useState('');
    const [notes, setNotes] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);

    const handleSubmit = () => {
      if (!agreedDate) {
        setValidationError(m.cad_field_new_date_required());
        return;
      }
      if (!confirmedByName.trim() && !confirmedByPhone.trim()) {
        setValidationError(m.cad_confirmed_by_required());
        return;
      }
      setValidationError(null);
      onSubmit({
        agreedDate,
        securedSite,
        confirmedByName: confirmedByName.trim(),
        confirmedByPhone: confirmedByPhone.trim(),
        newContactName,
        newContactPhone,
        notes: notes.trim(),
      });
    };

    return ActionModalShell({
      iconClass: 'icon-[material-symbols--event-repeat-outline]',
      title: title ?? m.cad_modal_title(),
      subtitle: subtitle ?? m.cad_modal_subtitle(),
      onClose,
      body: html`
        ${error || validationError
          ? html`<div class="alert alert-error text-sm py-2">${error ?? validationError}</div>`
          : nothing}

        <div class="form-control">
          <label for="cad-new-date" class="text-sm font-medium pb-1">${m.cad_field_new_date()}</label>
          <input
            id="cad-new-date"
            name="cad-new-date"
            type="date"
            class="input input-bordered w-full"
            .value=${agreedDate}
            @input=${(e: InputEvent) => setAgreedDate((e.target as HTMLInputElement).value)}
          />
        </div>

        <div class="flex flex-col gap-2">
          <div>
            <span class="text-sm font-medium block">${m.cad_confirmed_by_title()}</span>
            <span class="text-xs text-base-content/50 block mt-0.5">${m.cad_confirmed_by_help()}</span>
          </div>
          <div class="form-control">
            <label for="cad-confirmed-by-name" class="text-sm font-medium pb-1"
              >${m.cad_field_confirmed_by_name()}</label
            >
            <input
              id="cad-confirmed-by-name"
              name="cad-confirmed-by-name"
              type="text"
              autocomplete="off"
              class="input input-bordered w-full"
              .value=${confirmedByName}
              @input=${(e: InputEvent) => {
                setConfirmedByName((e.target as HTMLInputElement).value);
                setValidationError(null);
              }}
            />
          </div>
          <div class="form-control">
            <label for="cad-confirmed-by-phone" class="text-sm font-medium pb-1"
              >${m.cad_field_confirmed_by_phone()}</label
            >
            <input
              id="cad-confirmed-by-phone"
              name="cad-confirmed-by-phone"
              type="tel"
              autocomplete="off"
              class="input input-bordered w-full"
              .value=${confirmedByPhone}
              @input=${(e: InputEvent) => {
                setConfirmedByPhone((e.target as HTMLInputElement).value);
                setValidationError(null);
              }}
            />
          </div>
        </div>

        ${showSecuredSite
          ? html`
              <div class="form-control">
                <label class="cursor-pointer flex items-start justify-between gap-3">
                  <span class="flex-1">
                    <span class="text-sm font-medium block">${m.cad_field_secured_site()}</span>
                    <span class="text-xs text-base-content/50 block mt-0.5">${m.cad_field_secured_site_help()}</span>
                  </span>
                  <input
                    type="checkbox"
                    name="cad-secured-site"
                    class="toggle toggle-primary mt-0.5 shrink-0"
                    ?checked=${securedSite}
                    @change=${(e: Event) => setSecuredSite((e.target as HTMLInputElement).checked)}
                  />
                </label>
              </div>
            `
          : nothing}

        <div class="form-control">
          <label for="cad-contact-name" class="text-sm font-medium pb-1">${m.cad_field_new_contact_name()}</label>
          <input
            id="cad-contact-name"
            name="cad-contact-name"
            type="text"
            autocomplete="name"
            class="input input-bordered w-full"
            .value=${newContactName}
            @input=${(e: InputEvent) => setNewContactName((e.target as HTMLInputElement).value)}
          />
        </div>

        <div class="form-control">
          <label for="cad-contact-phone" class="text-sm font-medium pb-1">${m.cad_field_new_contact_phone()}</label>
          <input
            id="cad-contact-phone"
            name="cad-contact-phone"
            type="tel"
            autocomplete="tel"
            class="input input-bordered w-full"
            .value=${newContactPhone}
            @input=${(e: InputEvent) => setNewContactPhone((e.target as HTMLInputElement).value)}
          />
        </div>

        ${showNotes ? NotesField({ id: 'cad-notes', value: notes, busy, onInput: setNotes }) : nothing}
      `,
      footer: html`
        <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${onClose}>
          ${m.dialog_cancel()}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm gap-1"
          ?disabled=${busy}
          @click=${handleSubmit}
        >
          ${busy
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : html`<span class="icon-[material-symbols--check] text-base"></span>`}
          ${busy ? m.modal_submit_busy() : (submitLabel ?? m.cad_submit())}
        </button>
      `,
    });
  },
);

/**
 * CUS — Just-Go visit unsuccessful, optionally with a CAD-style reschedule.
 *
 * Two-step UX in a single modal:
 *   Step 1: confirms the FE wants to log Just-Go-unsuccessful and asks if a new date was arranged.
 *   Step 2: only when "Yes" is picked — shows the CAD form. Submit fires both events;
 *           "No" submits just the unsuccessful_visit and closes.
 */
export interface CusModalSubmit {
  arrangedNewDate: false;
  notes: string;
}
export interface CusModalSubmitWithReschedule {
  arrangedNewDate: true;
  cad: CadModalValues;
  notes: string;
}

export const CusModal = stateful(
  ({
    busy,
    error,
    onClose,
    onSubmit,
    allowReschedule = true,
    showSecuredSite = true,
  }: {
    busy: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (values: CusModalSubmit | CusModalSubmitWithReschedule) => void;
    /** The reschedule step fires a CAD — hidden when the customer disabled CAD. */
    allowReschedule?: boolean;
    /** Passed through to the CAD form (the toggle fires CRA). */
    showSecuredSite?: boolean;
  }) => {
    const [step, setStep] = useState<'choose' | 'reschedule'>('choose');
    const [notes, setNotes] = useState('');

    if (step === 'reschedule' && allowReschedule) {
      // Re-uses the CAD form but with CUS-specific copy at the top so the FE
      // understands they're submitting both `unsuccessful_visit` and `customer_requested_reschedule`.
      // Notes were already collected in the first step (they describe why the visit failed,
      // so they ride on `unsuccessful_visit`) — hide the CAD form's own notes field.
      return CadModal({
        busy,
        error,
        onClose: () => setStep('choose'),
        onSubmit: cad => onSubmit({ arrangedNewDate: true, cad, notes: notes.trim() }),
        title: m.cus_modal_title(),
        subtitle: m.cad_modal_subtitle(),
        submitLabel: m.cus_submit(),
        showNotes: false,
        showSecuredSite,
      });
    }

    return ActionModalShell({
      iconClass: 'icon-[material-symbols--directions-walk-outline]',
      title: m.cus_modal_title(),
      subtitle: m.cus_modal_intro(),
      onClose,
      body: html`
        ${error ? html`<div class="alert alert-error text-sm py-2">${error}</div>` : nothing}
        ${NotesField({ id: 'cus-notes', value: notes, busy, onInput: setNotes })}
        ${allowReschedule
          ? html`<p class="text-sm text-base-content/80">${m.cus_arranged_date_question()}</p>`
          : nothing}
      `,
      footer: allowReschedule
        ? html`
            <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${onClose}>
              ${m.dialog_cancel()}
            </button>
            <button
              type="button"
              class="btn btn-outline btn-sm gap-1"
              ?disabled=${busy}
              @click=${() => onSubmit({ arrangedNewDate: false, notes: notes.trim() })}
            >
              ${busy
                ? html`<span class="loading loading-spinner loading-xs"></span>`
                : html`<span class="icon-[material-symbols--close] text-base"></span>`}
              ${m.ticket_review_no()}
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm gap-1"
              ?disabled=${busy}
              @click=${() => setStep('reschedule')}
            >
              <span class="icon-[material-symbols--arrow-forward] text-base"></span>
              ${m.ticket_review_yes()}
            </button>
          `
        : html`
            <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${onClose}>
              ${m.dialog_cancel()}
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm gap-1"
              ?disabled=${busy}
              @click=${() => onSubmit({ arrangedNewDate: false, notes: notes.trim() })}
            >
              ${busy
                ? html`<span class="loading loading-spinner loading-xs"></span>`
                : html`<span class="icon-[material-symbols--send-outline] text-base"></span>`}
              ${busy ? m.modal_submit_busy() : m.cus_submit()}
            </button>
          `,
    });
  },
);

/** CNA — customer not available (without going on site). */
export interface CnaModalValues {
  attempts: number;
  result: 'NO_ANSWER';
  /** Phone numbers the FE called — at least one is required. */
  numbers: string[];
  /** When the (last) call was made. */
  callTime: Date;
  notes: string;
}

export const CnaModal = stateful(
  ({
    busy,
    error,
    onClose,
    onSubmit,
  }: {
    busy: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (values: CnaModalValues) => void;
  }) => {
    const [attempts, setAttempts] = useState(1);
    const [numbers, setNumbers] = useState<string[]>(['']);
    const [callTimeValue, setCallTimeValue] = useState<string>(dateToLocalInputValue(new Date()));
    const [notes, setNotes] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);

    const setNumberAt = (index: number, value: string) => {
      setNumbers(numbers.map((n, i) => (i === index ? value : n)));
      setValidationError(null);
    };

    const handleSubmit = () => {
      const cleaned = numbers.map(n => n.trim()).filter(Boolean);
      if (!cleaned.length) {
        setValidationError(m.cna_numbers_required());
        return;
      }
      const callTime = callTimeValue ? new Date(callTimeValue) : null;
      if (!callTime || Number.isNaN(callTime.getTime())) {
        setValidationError(m.workflow_time_invalid());
        return;
      }
      if (callTime.getTime() > Date.now() + 60_000) {
        setValidationError(m.workflow_time_future());
        return;
      }
      setValidationError(null);
      onSubmit({ attempts, result: 'NO_ANSWER', numbers: cleaned, callTime, notes: notes.trim() });
    };

    return ActionModalShell({
      iconClass: 'icon-[material-symbols--phone-disabled-outline]',
      title: m.cna_modal_title(),
      subtitle: m.cna_modal_subtitle(),
      onClose,
      body: html`
        ${error || validationError
          ? html`<div class="alert alert-error text-sm py-2">${error ?? validationError}</div>`
          : nothing}
        <div class="form-control">
          <label for="cna-number-0" class="text-sm font-medium pb-1">${m.cna_field_numbers()}</label>
          <div class="flex flex-col gap-2">
            ${numbers.map(
              (number, index) => html`
                <div class="flex gap-2">
                  <input
                    id="cna-number-${index}"
                    name="cna-number-${index}"
                    type="tel"
                    autocomplete="off"
                    class="input input-bordered w-full"
                    placeholder="${m.cna_field_numbers_placeholder()}"
                    ?disabled=${busy}
                    .value=${number}
                    @input=${(e: InputEvent) => setNumberAt(index, (e.target as HTMLInputElement).value)}
                  />
                  ${numbers.length > 1
                    ? html`
                        <button
                          type="button"
                          class="btn btn-ghost btn-sm btn-circle self-center"
                          aria-label="${m.cna_remove_number()}"
                          ?disabled=${busy}
                          @click=${() => {
                            setNumbers(numbers.filter((_, i) => i !== index));
                            setValidationError(null);
                          }}
                        >
                          <span class="icon-[material-symbols--close]"></span>
                        </button>
                      `
                    : nothing}
                </div>
              `,
            )}
          </div>
          <button
            type="button"
            class="btn btn-ghost btn-xs gap-1 text-primary self-start mt-2"
            ?disabled=${busy}
            @click=${() => setNumbers([...numbers, ''])}
          >
            <span class="icon-[material-symbols--add] text-base"></span>
            ${m.cna_add_number()}
          </button>
        </div>
        <div class="form-control">
          <label for="cna-attempts" class="text-sm font-medium pb-1">${m.cna_field_attempts()}</label>
          <input
            id="cna-attempts"
            name="cna-attempts"
            type="number"
            min="1"
            step="1"
            inputmode="numeric"
            class="input input-bordered w-full"
            .value=${String(attempts)}
            @input=${(e: InputEvent) => {
              const n = parseInt((e.target as HTMLInputElement).value, 10);
              setAttempts(Number.isFinite(n) && n >= 1 ? n : 1);
            }}
          />
        </div>
        <div class="form-control">
          <label for="cna-call-time" class="text-sm font-medium pb-1">${m.cna_field_call_time()}</label>
          <input
            id="cna-call-time"
            name="cna-call-time"
            type="datetime-local"
            class="input input-bordered w-full tabular-nums"
            ?disabled=${busy}
            .value=${callTimeValue}
            @input=${(e: InputEvent) => {
              setCallTimeValue((e.target as HTMLInputElement).value);
              setValidationError(null);
            }}
          />
          <p class="text-xs text-base-content/50 mt-1.5">${m.cna_field_call_time_help()}</p>
        </div>
        <div class="form-control">
          <label class="text-sm font-medium pb-1">${m.cna_field_result()}</label>
          <p class="text-sm text-base-content/70">${m.cna_result_no_answer()}</p>
        </div>
        ${NotesField({ id: 'cna-notes', value: notes, busy, onInput: setNotes })}
      `,
      footer: html`
        <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${onClose}>
          ${m.dialog_cancel()}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm gap-1"
          ?disabled=${busy}
          @click=${handleSubmit}
        >
          ${busy
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : html`<span class="icon-[material-symbols--send-outline] text-base"></span>`}
          ${busy ? m.modal_submit_busy() : m.cna_submit()}
        </button>
      `,
    });
  },
);

/**
 * TPM — catch-all for problems that don't fit any of the other report types.
 * Unlike the other exception modals the notes are MANDATORY: the description is the
 * whole report, so the service desk gets nothing actionable without it.
 */
export const TpmModal = stateful(
  ({
    busy,
    error,
    onClose,
    onSubmit,
  }: {
    busy: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (notes: string) => void;
  }) => {
    const [notes, setNotes] = useState('');
    const canSubmit = notes.trim().length > 0;

    return ActionModalShell({
      iconClass: 'icon-[material-symbols--handyman-outline]',
      title: m.tpm_modal_title(),
      subtitle: m.tpm_modal_subtitle(),
      onClose,
      body: html`
        ${error ? html`<div class="alert alert-error text-sm py-2">${error}</div>` : nothing}
        <div class="form-control">
          <label for="tpm-notes" class="text-sm font-medium pb-1">${m.tpm_field_notes()}</label>
          <textarea
            id="tpm-notes"
            name="tpm-notes"
            class="textarea textarea-bordered w-full"
            rows="4"
            placeholder="${m.tpm_field_notes_placeholder()}"
            ?disabled=${busy}
            .value=${notes}
            @input=${(e: Event) => setNotes((e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </div>
      `,
      footer: html`
        <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${onClose}>
          ${m.dialog_cancel()}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm gap-1"
          ?disabled=${busy || !canSubmit}
          @click=${() => onSubmit(notes.trim())}
        >
          ${busy
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : html`<span class="icon-[material-symbols--send-outline] text-base"></span>`}
          ${busy ? m.modal_submit_busy() : m.tpm_submit()}
        </button>
      `,
    });
  },
);

/** CRA — customer refused access on site, no rescheduled date. */
export const CraModal = stateful(
  ({
    busy,
    error,
    onClose,
    onSubmit,
  }: {
    busy: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (notes: string) => void;
  }) => {
    const [notes, setNotes] = useState('');

    return ActionModalShell({
      iconClass: 'icon-[material-symbols--lock-outline]',
      title: m.cra_modal_title(),
      subtitle: m.cra_modal_subtitle(),
      onClose,
      body: html`
        ${error ? html`<div class="alert alert-error text-sm py-2">${error}</div>` : nothing}
        <div class="alert alert-warning text-sm">
          <span class="icon-[material-symbols--warning-outline] text-lg"></span>
          <span>${m.cra_modal_warning()}</span>
        </div>
        ${NotesField({ id: 'cra-notes', value: notes, busy, onInput: setNotes })}
      `,
      footer: html`
        <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${onClose}>
          ${m.dialog_cancel()}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm gap-1"
          ?disabled=${busy}
          @click=${() => onSubmit(notes.trim())}
        >
          ${busy
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : html`<span class="icon-[material-symbols--check] text-base"></span>`}
          ${busy ? m.modal_submit_busy() : m.cra_submit()}
        </button>
      `,
    });
  },
);

/** Request cancellation — free-text reason field. */
export const CancellationModal = stateful(
  ({
    busy,
    error,
    onClose,
    onSubmit,
  }: {
    busy: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (reason: string) => void;
  }) => {
    const [reason, setReason] = useState('');
    const canSubmit = reason.trim().length > 0;

    return ActionModalShell({
      iconClass: 'icon-[material-symbols--cancel-outline]',
      title: m.cancel_request_modal_title(),
      subtitle: m.cancel_request_modal_subtitle(),
      onClose,
      body: html`
        ${error ? html`<div class="alert alert-error text-sm py-2">${error}</div>` : nothing}
        <div class="form-control">
          <label for="cancel-reason" class="text-sm font-medium pb-1">${m.cancel_request_field_reason()}</label>
          <textarea
            id="cancel-reason"
            name="cancel-reason"
            class="textarea textarea-bordered w-full"
            rows="4"
            placeholder="${m.cancel_request_field_reason_placeholder()}"
            .value=${reason}
            @input=${(e: Event) => setReason((e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </div>
      `,
      footer: html`
        <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${onClose}>
          ${m.dialog_cancel()}
        </button>
        <button
          type="button"
          class="btn btn-error btn-sm gap-1"
          ?disabled=${busy || !canSubmit}
          @click=${() => onSubmit(reason.trim())}
        >
          ${busy
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : html`<span class="icon-[material-symbols--cancel-outline] text-base"></span>`}
          ${busy ? m.modal_submit_busy() : m.cancel_request_submit()}
        </button>
      `,
    });
  },
);

/**
 * General comment — free-text the FE can leave at any time, recorded as a ServiceNow handling
 * note. The "Emergency" checkbox marks it high priority. Comment is mandatory.
 */
export const CommentModal = stateful(
  ({
    busy,
    error,
    onClose,
    onSubmit,
  }: {
    busy: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (comment: string, emergency: boolean) => void;
  }) => {
    const [comment, setComment] = useState('');
    const [emergency, setEmergency] = useState(false);
    const canSubmit = comment.trim().length > 0;

    return ActionModalShell({
      iconClass: 'icon-[material-symbols--chat-outline]',
      title: m.comment_modal_title(),
      onClose,
      body: html`
        ${error ? html`<div class="alert alert-error text-sm py-2">${error}</div>` : nothing}
        <div class="form-control">
          <label for="comment-text" class="text-sm font-medium pb-1">${m.comment_field_label()}</label>
          <textarea
            id="comment-text"
            name="comment-text"
            class="textarea textarea-bordered w-full"
            rows="4"
            placeholder="${m.comment_field_placeholder()}"
            ?disabled=${busy}
            .value=${comment}
            @input=${(e: Event) => setComment((e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </div>
        <label class="flex items-start gap-2 cursor-pointer mt-1">
          <input
            type="checkbox"
            class="checkbox checkbox-sm checkbox-error mt-0.5 shrink-0"
            ?disabled=${busy}
            .checked=${emergency}
            @change=${(e: Event) => setEmergency((e.target as HTMLInputElement).checked)}
          />
          <span>
            <span class="text-sm font-medium block">${m.comment_field_emergency()}</span>
            <span class="text-xs text-base-content/50 block">${m.comment_field_emergency_help()}</span>
          </span>
        </label>
      `,
      footer: html`
        <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${onClose}>
          ${m.dialog_cancel()}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm gap-1"
          ?disabled=${busy || !canSubmit}
          @click=${() => onSubmit(comment.trim(), emergency)}
        >
          ${busy
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : html`<span class="icon-[material-symbols--send-outline] text-base"></span>`}
          ${busy ? m.modal_submit_busy() : m.comment_submit()}
        </button>
      `,
    });
  },
);

/**
 * Workflow event-time modal — used for Accept, Travel Start / End and Intervention Start / End.
 *
 * The default value is the device's current local time, so the common "I'm pressing this
 * the moment it happened" path is one tap. The FE can backdate it (e.g. they actually left
 * 20 minutes ago and forgot to log it) before confirming. We never let them push the time
 * into the future — there's no real workflow case for it and it would just create bad data.
 *
 * `allowBackdate` (default true) gates the editable time field. When false — used for
 * accepting an assignment — there's no picker at all and submit always logs the current
 * moment, so an accept can't be back-dated.
 */
const pad2 = (n: number) => String(n).padStart(2, '0');

/** Format a Date as `YYYY-MM-DDTHH:MM` for the `<input type="datetime-local">` value. */
function dateToLocalInputValue(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
    d.getHours(),
  )}:${pad2(d.getMinutes())}`;
}

export const WorkflowTimestampModal = stateful(
  ({
    busy,
    error,
    title,
    iconClass,
    submitLabel,
    onClose,
    onSubmit,
    allowBackdate = true,
  }: {
    busy: boolean;
    error: string | null;
    title: string;
    iconClass: string;
    submitLabel: string;
    onClose: () => void;
    onSubmit: (at: Date) => void;
    allowBackdate?: boolean;
  }) => {
    const [value, setValue] = useState<string>(dateToLocalInputValue(new Date()));
    const [validationError, setValidationError] = useState<string | null>(null);

    const handleSubmit = () => {
      // Non-backdateable actions (accepting an assignment) have no picker — always log the
      // moment of confirmation, captured fresh on submit rather than when the modal opened.
      if (!allowBackdate) {
        setValidationError(null);
        onSubmit(new Date());
        return;
      }
      const parsed = value ? new Date(value) : null;
      if (!parsed || Number.isNaN(parsed.getTime())) {
        setValidationError(m.workflow_time_invalid());
        return;
      }
      if (parsed.getTime() > Date.now() + 60_000) {
        setValidationError(m.workflow_time_future());
        return;
      }
      setValidationError(null);
      onSubmit(parsed);
    };

    const setNow = () => {
      setValue(dateToLocalInputValue(new Date()));
      setValidationError(null);
    };

    return ActionModalShell({
      iconClass,
      title,
      subtitle: allowBackdate ? m.workflow_time_modal_subtitle() : m.workflow_time_locked_subtitle(),
      onClose,
      body: html`
        ${error || validationError
          ? html`<div class="alert alert-error text-sm py-2">${error ?? validationError}</div>`
          : nothing}

        ${allowBackdate
          ? html`
              <div class="form-control">
                <div class="flex items-baseline justify-between pb-1 gap-2">
                  <label for="wf-time" class="text-sm font-medium">${m.workflow_time_field_label()}</label>
                  <button
                    type="button"
                    class="btn btn-ghost btn-xs gap-1 text-primary"
                    ?disabled=${busy}
                    @click=${setNow}
                  >
                    <span class="icon-[material-symbols--update] text-base"></span>
                    ${m.workflow_time_now()}
                  </button>
                </div>
                <input
                  id="wf-time"
                  name="wf-time"
                  type="datetime-local"
                  class="input input-bordered w-full tabular-nums"
                  .value=${value}
                  @input=${(e: InputEvent) => {
                    setValue((e.target as HTMLInputElement).value);
                    setValidationError(null);
                  }}
                />
                <p class="text-xs text-base-content/50 mt-1.5">${m.workflow_time_field_help()}</p>
              </div>
            `
          : nothing}
      `,
      footer: html`
        <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${onClose}>
          ${m.dialog_cancel()}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm gap-1"
          ?disabled=${busy}
          @click=${handleSubmit}
        >
          ${busy
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : html`<span class="icon-[material-symbols--check] text-base"></span>`}
          ${busy ? m.modal_submit_busy() : submitLabel}
        </button>
      `,
    });
  },
);

/**
 * Travel Start variant of the workflow modal: the FE confirms when they actually
 * left (`at`, defaults to now, future rejected) and the single ETA timestamp the
 * customer will see (`eta`, spec §4 `en_route.payload.eta`, defaults to now + 30 min
 * rounded forward to the next 5-minute boundary). ETA must be at or after `at`.
 */
function roundUpTo5Min(date: Date): Date {
  const ms = 5 * 60_000;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
}

export const TravelStartModal = stateful(
  ({
    busy,
    error,
    onClose,
    onSubmit,
  }: {
    busy: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (values: { at: Date; eta: Date }) => void;
  }) => {
    const now = new Date();
    const defaultEta = roundUpTo5Min(new Date(now.getTime() + 30 * 60_000));

    const [atValue, setAtValue] = useState<string>(dateToLocalInputValue(now));
    const [etaValue, setEtaValue] = useState<string>(dateToLocalInputValue(defaultEta));
    const [validationError, setValidationError] = useState<string | null>(null);

    const handleSubmit = () => {
      const at = atValue ? new Date(atValue) : null;
      const eta = etaValue ? new Date(etaValue) : null;
      if (!at || Number.isNaN(at.getTime()) || !eta || Number.isNaN(eta.getTime())) {
        setValidationError(m.workflow_time_invalid());
        return;
      }
      if (at.getTime() > Date.now() + 60_000) {
        setValidationError(m.workflow_time_future());
        return;
      }
      if (eta.getTime() < at.getTime()) {
        setValidationError(m.travel_start_eta_before_event());
        return;
      }
      setValidationError(null);
      onSubmit({ at, eta });
    };

    const setAtNow = () => {
      setAtValue(dateToLocalInputValue(new Date()));
      setValidationError(null);
    };

    return ActionModalShell({
      iconClass: 'icon-[material-symbols--directions-car-outline]',
      title: m.ticket_action_travel_start(),
      subtitle: m.workflow_time_modal_subtitle(),
      onClose,
      body: html`
        ${error || validationError
          ? html`<div class="alert alert-error text-sm py-2">${error ?? validationError}</div>`
          : nothing}

        <div class="form-control">
          <div class="flex items-baseline justify-between pb-1 gap-2">
            <label for="ts-at" class="text-sm font-medium">${m.workflow_time_field_label()}</label>
            <button
              type="button"
              class="btn btn-ghost btn-xs gap-1 text-primary"
              ?disabled=${busy}
              @click=${setAtNow}
            >
              <span class="icon-[material-symbols--update] text-base"></span>
              ${m.workflow_time_now()}
            </button>
          </div>
          <input
            id="ts-at"
            name="ts-at"
            type="datetime-local"
            class="input input-bordered w-full tabular-nums"
            .value=${atValue}
            @input=${(e: InputEvent) => {
              setAtValue((e.target as HTMLInputElement).value);
              setValidationError(null);
            }}
          />
          <p class="text-xs text-base-content/50 mt-1.5">${m.workflow_time_field_help()}</p>
        </div>

        <div class="form-control">
          <label for="ts-eta" class="text-sm font-medium pb-1">${m.travel_start_eta_label()}</label>
          <input
            id="ts-eta"
            name="ts-eta"
            type="datetime-local"
            class="input input-bordered w-full tabular-nums"
            .value=${etaValue}
            @input=${(e: InputEvent) => {
              setEtaValue((e.target as HTMLInputElement).value);
              setValidationError(null);
            }}
          />
          <p class="text-xs text-base-content/50 mt-1.5">${m.travel_start_eta_help()}</p>
        </div>
      `,
      footer: html`
        <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${onClose}>
          ${m.dialog_cancel()}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm gap-1"
          ?disabled=${busy}
          @click=${handleSubmit}
        >
          ${busy
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : html`<span class="icon-[material-symbols--check] text-base"></span>`}
          ${busy ? m.modal_submit_busy() : m.ticket_action_travel_start()}
        </button>
      `,
    });
  },
);
